import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CORRIDORS } from '@/constants/laadplein-data';
import type { LaadpleinLocatie, LaadpleinData } from '@/types/laadplein';

type DbLocatie = {
  id: string;
  naam: string;
  adres: string | null;
  postcode: string | null;
  plaats: string | null;
  status: string | null;
  oppervlakte_m2: number | null;
  netaansluiting_kw: number | null;
  afstand_a_n_weg_km: number | null;
  score_bereikbaarheid: number | null;
  score_netcapaciteit: number | null;
  score_concurrentie: number | null;
  score_vraag: number | null;
  score_kosten: number | null;
  totaal_score: number | null;
  spula_eligible: boolean | null;
  op_bedrijventerrein: boolean | null;
  netcongestie_status: string | null;
  latitude: number | null;
  longitude: number | null;
  eigenaar: string | null;
  huidig_gebruik: string | null;
  huur_koop: string | null;
  postcodegebied_2: string | null;
  notities: string | null;
  toegangsweg_breedte_m: number | null;
  spula_blokkade: string | null;
  netbeheerder: string | null;
  ndw_meetpunt_ids: string[] | null;
  ocm_last_sync: string | null;
  ndw_last_sync: string | null;
  created_at: string | null;
  updated_at: string | null;
};

function dbToLocatie(row: DbLocatie): LaadpleinLocatie {
  // Find closest corridor by postcode/location match (best-effort)
  const corridor = CORRIDORS[0]; // Default; in Phase 2 we'll use geo-distance

  return {
    id: row.id,
    naam: row.naam,
    adres: row.adres ?? '',
    postcode: row.postcode ?? '',
    corridor: '', // No longer stored as corridor id — derived from location
    oppervlakte: row.oppervlakte_m2 ?? 0,
    net_kw: row.netaansluiting_kw ?? 0,
    afstand_weg: row.afstand_a_n_weg_km ?? 0,
    status: (row.status as any) ?? 'prospect',
    s_bereik: row.score_bereikbaarheid ?? 5,
    s_net: row.score_netcapaciteit ?? 5,
    s_concur: row.score_concurrentie ?? 5,
    s_vraag: row.score_vraag ?? 5,
    s_kosten: row.score_kosten ?? 5,
    totaal: row.totaal_score ?? 0,
    spula: row.spula_eligible ?? false,
    corridor_data: corridor,
    notities: row.notities ?? '',
  };
}

export function useLaadpleinData() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: locaties = [], isLoading } = useQuery({
    queryKey: ['laadplein-locaties-full'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('laadplein_locaties')
        .select('*')
        .order('totaal_score', { ascending: false, nullsFirst: false });

      if (error) throw error;
      return (data ?? []).map(row => dbToLocatie(row as DbLocatie));
    },
    staleTime: 60 * 1000,
  });

  const addMutation = useMutation({
    mutationFn: async (input: Omit<LaadpleinLocatie, 'id' | 'totaal' | 'spula' | 'corridor_data'>) => {
      const scores = [input.s_bereik, input.s_net, input.s_concur, input.s_vraag, input.s_kosten];
      const totaal = scores.reduce((a, b) => a + b, 0) / 5;
      const spula = input.net_kw >= 100 && input.afstand_weg <= 1;
      const pc2 = input.postcode ? input.postcode.substring(0, 2) : null;

      const { data, error } = await supabase
        .from('laadplein_locaties')
        .insert({
          naam: input.naam,
          adres: input.adres || null,
          postcode: input.postcode || null,
          postcodegebied_2: pc2,
          oppervlakte_m2: input.oppervlakte || null,
          netaansluiting_kw: input.net_kw || null,
          afstand_a_n_weg_km: input.afstand_weg,
          status: input.status || 'prospect',
          score_bereikbaarheid: input.s_bereik,
          score_netcapaciteit: input.s_net,
          score_concurrentie: input.s_concur,
          score_vraag: input.s_vraag,
          score_kosten: input.s_kosten,
          spula_eligible: spula,
          notities: input.notities || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties-full'] });
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties'] });
      toast({ title: 'Locatie opgeslagen', description: 'De locatie is toegevoegd aan de database.' });
    },
    onError: (error: any) => {
      console.error('Error adding locatie:', error);
      toast({ title: 'Fout bij opslaan', description: error.message, variant: 'destructive' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('laadplein_locaties')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties-full'] });
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties'] });
      toast({ title: 'Locatie verwijderd' });
    },
    onError: (error: any) => {
      console.error('Error removing locatie:', error);
      toast({ title: 'Fout bij verwijderen', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<LaadpleinLocatie> }) => {
      const dbUpdates: Record<string, any> = {};
      if (updates.naam !== undefined) dbUpdates.naam = updates.naam;
      if (updates.adres !== undefined) dbUpdates.adres = updates.adres || null;
      if (updates.postcode !== undefined) {
        dbUpdates.postcode = updates.postcode || null;
        dbUpdates.postcodegebied_2 = updates.postcode ? updates.postcode.substring(0, 2) : null;
      }
      if (updates.oppervlakte !== undefined) dbUpdates.oppervlakte_m2 = updates.oppervlakte || null;
      if (updates.net_kw !== undefined) dbUpdates.netaansluiting_kw = updates.net_kw || null;
      if (updates.afstand_weg !== undefined) dbUpdates.afstand_a_n_weg_km = updates.afstand_weg;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.s_bereik !== undefined) dbUpdates.score_bereikbaarheid = updates.s_bereik;
      if (updates.s_net !== undefined) dbUpdates.score_netcapaciteit = updates.s_net;
      if (updates.s_concur !== undefined) dbUpdates.score_concurrentie = updates.s_concur;
      if (updates.s_vraag !== undefined) dbUpdates.score_vraag = updates.s_vraag;
      if (updates.s_kosten !== undefined) dbUpdates.score_kosten = updates.s_kosten;
      if (updates.notities !== undefined) dbUpdates.notities = updates.notities || null;

      // Recalculate derived fields if scores changed
      const s = [
        updates.s_bereik ?? undefined,
        updates.s_net ?? undefined,
        updates.s_concur ?? undefined,
        updates.s_vraag ?? undefined,
        updates.s_kosten ?? undefined,
      ];
      if (s.some(v => v !== undefined)) {
        // We need current values for any score not being updated, so skip auto-calc here
        // totaal_score is a GENERATED column in the DB — it auto-calculates
      }

      if (updates.net_kw !== undefined || updates.afstand_weg !== undefined) {
        const nk = updates.net_kw ?? 0;
        const aw = updates.afstand_weg ?? 0;
        dbUpdates.spula_eligible = nk >= 100 && aw <= 1;
      }

      const { error } = await supabase
        .from('laadplein_locaties')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties-full'] });
      queryClient.invalidateQueries({ queryKey: ['laadplein-locaties'] });
    },
    onError: (error: any) => {
      console.error('Error updating locatie:', error);
      toast({ title: 'Fout bij bijwerken', description: error.message, variant: 'destructive' });
    },
  });

  // Backward-compatible data shape
  const data: LaadpleinData = { locaties };

  return {
    data,
    locaties,
    isLoading,
    addLocatie: (input: Omit<LaadpleinLocatie, 'id' | 'totaal' | 'spula' | 'corridor_data'>) => addMutation.mutate(input),
    removeLocatie: (id: string) => removeMutation.mutate(id),
    updateLocatie: (id: string, updates: Partial<LaadpleinLocatie>) => updateMutation.mutate({ id, updates }),
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  };
}
